import { etagFromRow, get, getVersioned, postVersioned, seg } from '../client';
import type { ListParams, Page } from '../types/common';
import type {
  CloseResultRequest, CommunicateResultRequest, CreateFollowUpRequest, FollowUp, QuarantinedResult, ReconciliationPatient, ReleaseResultRequest, Result, ResultActionRequest,
  ResultSummary, ResultsImportRequest, ResultsImportResponse, ReviewResultRequest,
} from '../types/results';

const rcmd = (id: string, c: string) => `/results/${seg(id)}:${c}`;

/** Prefer the response ETag; fall back to the body's rowVersion (list items and CORS-hidden headers). */
const withTag = <T extends { rowVersion?: number }>(r: { data: T; etag: string | null }) => ({ data: r.data, etag: r.etag ?? etagFromRow(r.data?.rowVersion) });

/** Stable sender message id for an import payload: same payload → same id → the backend replays instead of duplicating. */
export function importMessageId(source: string, results: unknown[]): string {
  const text = `${source}\n${JSON.stringify(results)}`;
  let h1 = 0x811c9dc5, h2 = 0x01000193;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x5bd1e995) >>> 0;
  }
  return `ui-import-${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

export const resultsApi = {
  /** sourceMessageId defaults to a hash of the payload, so re-sending the same payload is a replay, not a duplicate. */
  import: async (body: Omit<ResultsImportRequest, 'sourceMessageId'> & { sourceMessageId?: string }) =>
    (
      await postVersioned<ResultsImportResponse>(
        '/results:import',
        { ...body, sourceMessageId: body.sourceMessageId?.trim() || importMessageId(body.source, body.results) },
        { idempotent: true },
      )
    ).data,
  quarantine: (p: ListParams & { status?: 'open' | 'resolved' | 'all' } = {}) => {
    const { status, ...rest } = p;
    return get<Page<QuarantinedResult>>('/results/quarantine', { ...rest, status: status && status !== 'all' ? status : undefined });
  },
  quarantinePatients: (id: string, p: ListParams & { q: string }) =>
    get<Page<ReconciliationPatient>>(`/results/quarantine/${seg(id)}/patients`, { ...p }),
  /** If-Match: the quarantine row's version (`W/"<rowVersion>"` from the list item). */
  resolveQuarantine: async (id: string, patientId: string, reason: string, etag: string | null) =>
    (await postVersioned<QuarantinedResult>(`/results/quarantine/${seg(id)}:resolve`, { patientId, reason }, { ifMatch: etag, idempotent: true })).data,
  list: (patientId: string, p: ListParams & { status?: string; category?: string } = {}) => {
    const { status, ...rest } = p;
    return get<Page<ResultSummary>>(`/patients/${seg(patientId)}/results`, { ...rest, status: status && status !== 'all' ? status : undefined });
  },
  get: async (id: string, chartPatientId?: string) => withTag(await getVersioned<Result>(`/results/${seg(id)}`, { chartPatientId })),
  /** Acknowledge = "I have seen it". Not review. (RES-004 / HF-005) */
  acknowledge: async (id: string, etag: string | null) => withTag(await postVersioned<Result>(rcmd(id, 'acknowledge'), {}, { ifMatch: etag, idempotent: true })),
  review: async (id: string, body: ReviewResultRequest, etag: string | null) =>
    withTag(await postVersioned<Result>(rcmd(id, 'review'), body, { ifMatch: etag, idempotent: true })),
  communicate: async (id: string, body: CommunicateResultRequest, etag: string | null) =>
    withTag(await postVersioned<Result>(rcmd(id, 'communicate'), body, { ifMatch: etag, idempotent: true })),
  /** Step-up. Backend requires BOTH clinicianNote and plainLanguage. */
  release: async (id: string, body: ReleaseResultRequest, etag: string | null, label: string) =>
    withTag(await postVersioned<Result>(rcmd(id, 'release'), body, { ifMatch: etag, idempotent: true, stepUpLabel: label })),
  correct: async (id: string, body: { observations: unknown[]; reason: string }, etag: string | null) =>
    withTag(await postVersioned<Result>(rcmd(id, 'correct'), body, { ifMatch: etag, idempotent: true })),
  /** Record a clinical action on the result (repeatable; does not close the loop). */
  action: async (id: string, body: ResultActionRequest, etag: string | null) =>
    withTag(await postVersioned<Result>(rcmd(id, 'action'), body, { ifMatch: etag, idempotent: true })),
  /** Close the loop explicitly. Also resolves the result's work item. 409 until review, communication, critical ack and follow-ups are done. */
  close: async (id: string, body: CloseResultRequest, etag: string | null) =>
    withTag(await postVersioned<Result>(rcmd(id, 'close'), body, { ifMatch: etag, idempotent: true })),
  followUps: (id: string) => get<Page<FollowUp>>(`/results/${seg(id)}/follow-ups`),
  /** If-Match: the RESULT's ETag (adding a follow-up bumps the result version). */
  addFollowUp: async (id: string, body: CreateFollowUpRequest, resultEtag: string | null) =>
    (await postVersioned<FollowUp>(`/results/${seg(id)}/follow-ups`, body, { ifMatch: resultEtag, idempotent: true })).data,
  /** If-Match: the follow-up's own version. */
  completeFollowUp: async (id: string, note: string | undefined, etag: string | null) =>
    (await postVersioned<FollowUp>(`/follow-ups/${seg(id)}:complete`, note ? { note } : {}, { ifMatch: etag, idempotent: true })).data,
};
